import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { ArrowLeft, Clock } from "lucide-react";
import { Helmet } from "@dr.pogodin/react-helmet";
import { supabase } from "@/integrations/supabase/client";
import { MarkdownRenderer } from "@/components/blog/MarkdownRenderer";
import { ReadingProgress } from "@/components/blog/ReadingProgress";
import { BlogShareBar } from "@/components/blog/BlogShareBar";
import { BlogSEOHead } from "@/components/blog/BlogSEOHead";
import { BlogPostCard } from "@/components/blog/BlogPostCard";
import { BlogNewsletterSignup } from "@/components/blog/BlogNewsletterSignup";
import { CoverPattern } from "@/components/blog/CoverPattern";
import { AnimatedReveal } from "@/components/hero/shared/AnimatedReveal";
import { Logo } from "@/components/lembra/Logo";
import type { BlogPostWithRelations } from "@/types/blog";
import { usePublicBlogPostBySlug } from "@/hooks/queries";

const trackBadge: Record<string, { label: string; className: string }> = {
  office: { label: "Office", className: "bg-sky/20 text-navy" },
  kitchen: { label: "Kitchen", className: "bg-butter text-navy" },
  both: { label: "Both", className: "bg-charcoal/10 text-charcoal" },
};

export default function BlogPost() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const isPreview = searchParams.get("preview") === "true";

  // Primary post lookup comes from the shared hook; related posts stay
  // inline because the "explicit curation vs same-category fallback"
  // flow has enough branching to not be worth a dedicated hook.
  const postQuery = usePublicBlogPostBySlug(slug, isPreview);
  const post = postQuery.data ?? null;
  const loading = postQuery.isLoading;

  const [relatedPosts, setRelatedPosts] = useState<BlogPostWithRelations[]>([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    // Treat "query resolved with no match" as not-found; the hook returns
    // null in that case (maybeSingle inside the queryFn).
    if (!postQuery.isLoading && postQuery.isSuccess && post === null) {
      setNotFound(true);
    }
  }, [postQuery.isLoading, postQuery.isSuccess, post]);

  useEffect(() => {
    if (!post) return;

    const loadRelated = async () => {
      // MS397: wrap in try/catch — related posts are purely decorative;
      // a fetch failure should not break the article page or produce
      // unhandled-rejection noise in error monitoring.
      try {
        const { data: relatedIds } = await supabase
          .from("blog_related_posts")
          .select("related_post_id")
          .eq("post_id", post.id)
          .order("sort_order")
          .limit(3);

        if (relatedIds && relatedIds.length > 0) {
          const ids = relatedIds.map((r) => r.related_post_id);
          const { data: related } = await supabase
            .from("blog_posts")
            .select("*, blog_authors(name, slug, avatar_url), blog_categories(name, slug)")
            .in("id", ids)
            .eq("status", "published");
          if (related) setRelatedPosts(related as BlogPostWithRelations[]);
        } else if (post.category_id) {
          const { data: related } = await supabase
            .from("blog_posts")
            .select("*, blog_authors(name, slug, avatar_url), blog_categories(name, slug)")
            .eq("status", "published")
            .eq("category_id", post.category_id)
            .neq("id", post.id)
            .order("published_at", { ascending: false })
            .limit(3);
          if (related) setRelatedPosts(related as BlogPostWithRelations[]);
        }
      } catch {
        // Non-fatal — related posts simply won't appear.
      }
    };

    loadRelated();
  }, [post]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-charcoal/40">Loading…</p>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center gap-4">
        <Helmet><title>Post not found | Lembra Blog</title></Helmet>
        <h1 className="text-2xl font-heading text-navy">Post not found</h1>
        <Link to="/blog" className="text-sm text-coral hover:underline">← Back to blog</Link>
      </div>
    );
  }

  const t = trackBadge[post.track] || trackBadge.both;
  const authorName = post.blog_authors?.name || "Lembra";
  const authorAvatar = post.blog_authors?.avatar_url;
  const categoryName = post.blog_categories?.name;
  // blog_post_tags join shape depends on select args; keep the `any` narrow
  // to this one access pattern rather than typing the full join tree.
  const tags = (post.blog_post_tags as Array<{ blog_tags: unknown }> | undefined)
    ?.map((pt) => pt.blog_tags)
    .filter(Boolean) || [];

  return (
    <div className="min-h-screen bg-white">
      <BlogSEOHead post={post} />
      <ReadingProgress />

      {/* Preview banner */}
      {isPreview && post.status !== "published" && (
        <div className="bg-butter text-navy text-center py-2 text-sm font-semibold sticky top-0 z-50">
          Preview mode: this post is not published
        </div>
      )}

      {/* Header */}
      <header className="py-6 px-6 md:px-10">
        <Logo size="md" />
      </header>

      {/* Article header */}
      <section className="bg-cream py-12 md:py-16 px-6">
        <div className="max-w-[720px] mx-auto">
          <AnimatedReveal animation="fadeInUpSoft" duration={700}>
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-sm text-charcoal/50 hover:text-navy transition-colors mb-6"
            >
              <ArrowLeft className="h-4 w-4" /> Back to all posts
            </Link>

            {/* Category + Track */}
            <div className="flex items-center gap-2 mb-4">
              {categoryName && (
                <span className="text-xs font-semibold uppercase tracking-[0.05em] text-coral">
                  {categoryName}
                </span>
              )}
              <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${t.className}`}>
                {t.label}
              </span>
            </div>

            {/* Title */}
            <h1 className="font-heading text-3xl md:text-[2.5rem] text-navy leading-[1.15]">
              {post.title}
            </h1>

            {/* Excerpt */}
            {post.excerpt && (
              <p className="font-display italic text-lg md:text-xl text-charcoal/60 mt-4 leading-relaxed">
                {post.excerpt}
              </p>
            )}

            {/* Author bar */}
            <div className="flex items-center gap-3 mt-6 text-sm text-charcoal/60">
              {authorAvatar ? (
                <img src={authorAvatar} alt={authorName} className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <div className="h-8 w-8 rounded-full bg-sky/20 flex items-center justify-center text-navy font-bold text-sm">
                  {authorName.charAt(0)}
                </div>
              )}
              <div>
                <span className="font-semibold text-navy">{authorName}</span>
                <div className="flex items-center gap-2 text-xs text-charcoal/50">
                  {post.published_at && (
                    <span>{format(parseISO(post.published_at), "d MMMM yyyy")}</span>
                  )}
                  <span>·</span>
                  <span className="flex items-center gap-0.5">
                    <Clock className="h-3 w-3" /> {post.reading_time_minutes} min read
                  </span>
                </div>
              </div>
            </div>
          </AnimatedReveal>
        </div>
      </section>

      {/* Cover image */}
      {post.cover_image_url ? (
        <div className="max-w-[720px] mx-auto px-6 -mt-2 mb-8">
          <img
            src={post.cover_image_url}
            alt={post.cover_image_alt || post.title}
            className="w-full rounded-2xl aspect-video object-cover"
          />
        </div>
      ) : (
        <div className="max-w-[720px] mx-auto px-6 -mt-2 mb-8">
          <CoverPattern track={post.track} seed={post.slug} className="rounded-2xl" />
        </div>
      )}

      {/* Article body */}
      <article className="max-w-[720px] mx-auto px-6 pb-12">
        <MarkdownRenderer content={post.content} />

        {/* Tags */}
        {tags.length > 0 && (
          <div className="flex items-center gap-2 mt-10 pt-6 border-t border-charcoal/10">
            {(tags as Array<{ slug: string; name: string }>).map((tag) => (
              <span key={tag.slug} className="text-xs bg-charcoal/5 text-charcoal/60 px-3 py-1 rounded-full">
                {tag.name}
              </span>
            ))}
          </div>
        )}

        {/* Share */}
        <div className="flex items-center justify-between mt-8 pt-6 border-t border-charcoal/10">
          <span className="text-sm font-semibold text-navy">Share this post</span>
          <BlogShareBar title={post.title} url={`/blog/${post.slug}`} />
        </div>
      </article>

      {/* Related posts */}
      {relatedPosts.length > 0 && (
        <section className="bg-cream py-12 md:py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <h2 className="font-heading text-2xl text-navy mb-8 text-center">You might also like</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {relatedPosts.map((rp, i) => (
                <AnimatedReveal key={rp.id} animation="fadeInUp" duration={600} delay={i * 100}>
                  <BlogPostCard post={rp} />
                </AnimatedReveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter signup */}
      <section className="py-10 md:py-14 px-6">
        <div className="max-w-xl mx-auto">
          <BlogNewsletterSignup sourcePostSlug={post.slug} />
        </div>
      </section>

      {/* Product CTA */}
      <section className="pb-12 md:pb-16 px-6">
        <div className="max-w-xl mx-auto text-center">
          <h3 className="font-heading text-xl text-navy mb-3">Every birthday. Every anniversary. Sorted.</h3>
          <p className="text-charcoal/60 text-sm mb-6">
            Lembra handles every birthday and milestone so your team feels celebrated, without lifting a finger.
          </p>
          <Link
            to="/"
            className="inline-block px-6 py-2.5 rounded-full bg-coral text-white font-semibold text-sm hover:bg-coral/90 transition-colors"
          >
            Learn more
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-charcoal/10 py-8 px-6 text-center">
        <Link to="/blog" className="text-sm text-charcoal/50 hover:text-coral transition-colors">
          ← Back to blog
        </Link>
      </footer>
    </div>
  );
}
